import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { color, mono } from '../tokens';
import { type Quote } from '../fixtures';
import { useSynth, useSynthNav } from '../SynthesisContext';
import { asText } from '../asText';
import { countOf } from '../../../utils/pluralize';

function themeLabel(rank: number) {
  return `Theme ${String(rank).padStart(2, '0')}`;
}

function QuoteCard({ q }: { q: Quote }) {
  const who = asText(q.who).trim();
  const role = asText(q.role).trim();
  const file = asText(q.file).trim();
  const fileLooksUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(file);
  const segments = [who, role].filter(Boolean);
  const shownFile = file && (!fileLooksUuid || segments.length === 0) ? file : '';
  const meta = shownFile ? [...segments, shownFile] : segments;
  return (
    <div style={{
      background: color.surface, border: `1px solid ${color.hairline}`,
      borderLeft: `3px solid ${color.purple}`, borderRadius: 10,
      padding: '16px 18px', marginBottom: 12,
    }}>
      <p style={{ margin: 0, fontSize: 15, color: color.ink, lineHeight: 1.5 }}>
        &ldquo;{asText(q.text)}&rdquo;
      </p>
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {meta.length > 0 ? (
          <span style={{ fontSize: 13, color: color.textMuted }}>
            {meta.map((part, idx) => (
              <span key={`${part}-${idx}`}>
                {idx > 0 && <span style={{ color: color.separator, margin: '0 6px' }}>·</span>}
                <span style={{ fontSize: idx === 0 ? 13 : 12.5, fontWeight: idx === 0 ? 600 : 400, fontFamily: idx === meta.length - 1 && part === shownFile ? mono : 'inherit', color: idx === 0 ? color.text : color.textMuted }}>
                  {part}
                </span>
              </span>
            ))}
          </span>
        ) : (
          <span style={{ fontSize: 12.5, color: color.textFaint }}>Unknown attribution</span>
        )}
        <span style={{
          marginLeft: 'auto', fontSize: 11.5, fontFamily: mono, padding: '2px 8px', borderRadius: 6,
          background: color.pillActiveBg, color: color.purpleDeep,
        }}>
          {themeLabel(q.themeIdx)}
        </span>
      </div>
    </div>
  );
}

export default function Quotes() {
  const { quotes: fixtureQuotes, totalQuotes, themes } = useSynth();
  const { quoteFilter, setQuoteFilter } = useSynthNav();
  const [query, setQuery] = useState('');
  const filterThemes = useMemo(
    () => Array.from(new Set(fixtureQuotes.map((q) => q.themeIdx).filter((n) => Number.isFinite(n) && n > 0))).sort((a, b) => a - b),
    [fixtureQuotes],
  );
  const themeFilter = quoteFilter !== 'all' && !filterThemes.includes(quoteFilter) ? 'all' : quoteFilter;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return fixtureQuotes.filter((it) => {
      if (themeFilter !== 'all' && it.themeIdx !== themeFilter) return false;
      if (!q) return true;
      return (
        asText(it.text).toLowerCase().includes(q) ||
        asText(it.who).toLowerCase().includes(q) ||
        asText(it.role).toLowerCase().includes(q) ||
        asText(it.file).toLowerCase().includes(q)
      );
    });
  }, [query, themeFilter, fixtureQuotes]);

  const chipBase: React.CSSProperties = {
    fontSize: 12.5, padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
    border: `1px solid ${color.hairline}`, background: color.surface, color: color.textMuted,
    whiteSpace: 'nowrap',
  };
  const chipOn: React.CSSProperties = { background: color.ink, color: '#fff', border: '1px solid transparent' };

  return (
    <div>
      {/* Header block */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>TAB</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: color.purple,
          background: color.pillActiveBg, padding: '2px 8px', borderRadius: 6,
        }}>EVIDENCE</span>
      </div>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: color.ink, letterSpacing: '-0.01em' }}>
        Verbatim quotes with provenance
      </h2>
      <p style={{ margin: '6px 0 0', fontSize: 13, color: color.textMuted }}>
        Each quote cites source and which theme it supports.
      </p>
      <div style={{ height: 1, background: color.hairline, margin: '18px 0' }} />

      {/* Search + theme filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 260px',
          border: `1px solid ${color.hairline}`, borderRadius: 8, padding: '8px 12px', background: color.surface,
        }}>
          <Search size={15} color={color.textFaint} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search quotes…"
            style={{ border: 'none', outline: 'none', fontSize: 13, width: '100%', background: 'transparent', color: color.ink }}
          />
        </div>
        <button style={{ ...chipBase, ...(themeFilter === 'all' ? chipOn : {}) }} onClick={() => setQuoteFilter('all')}>
          All
        </button>
        {filterThemes.map((t) => (
          <button
            key={t}
            title={themes.find((th) => th.rank === t)?.name || ''}
            style={{ ...chipBase, ...(themeFilter === t ? chipOn : {}) }}
            onClick={() => setQuoteFilter(t)}
          >
            {themeLabel(t)}
          </button>
        ))}
      </div>

      {/* Quote cards */}
      {visible.length === 0 ? (
        <p style={{ fontSize: 13, color: color.textFaint, textAlign: 'center', padding: '40px 0' }}>
          No quotes match that filter.
        </p>
      ) : (
        visible.map((q, i) => <QuoteCard key={i} q={q} />)
      )}

      {/* Footer count */}
      <p style={{ marginTop: 8, fontSize: 12.5, color: color.textFaint, textAlign: 'center' }}>
        Showing {visible.length} of {countOf(totalQuotes, 'quote')}
      </p>
    </div>
  );
}
